/** Single namespace for all qa-studio v1 localStorage keys. */
const NS = 'qastudio.v1.';

export const StorageKeys = {
  user: NS + 'user',
  progress: NS + 'progress',
  ui: NS + 'ui',
  pomodoro: NS + 'pomodoro',
  quizAttempts: NS + 'quizAttempts',
  /**
   * Виджет практики на «Дом» (10 JUL 2026): показы/дисмиссы подсказок бадди.
   * Осознанно НЕ синкается между устройствами — это шум показа, а не прогресс.
   * Спека: COORDINATION/specs/home_practice_widget_copy.md §4.
   */
  nudges: NS + 'nudges',
} as const;

export type StorageKey = (typeof StorageKeys)[keyof typeof StorageKeys];

/** Type-safe read. Returns fallback if missing or JSON-parse fails. */
export function readStorage<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/** Type-safe write. Quota errors are swallowed (storage full = fail soft). */
export function writeStorage<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota exceeded — drop write silently */
  }
}

export function removeStorage(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * Remove every `qastudio.v1.*` key from localStorage — the app's own state and
 * caches — while leaving keys from other namespaces intact, notably the Supabase
 * auth token (`sb-<ref>-auth-token`). Used by the boot-recovery boundary to clear
 * corrupt/old-shape state WITHOUT logging the user out: progress re-syncs from the
 * server on next load. (Boot-guard · 13 JUN 2026.)
 */
export function clearAppNamespace(): void {
  if (typeof window === 'undefined') return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(NS)) keys.push(k);
    }
    for (const k of keys) window.localStorage.removeItem(k);
  } catch {
    /* storage blocked (private mode / disabled) — nothing to clear */
  }
}
